/**
 * CommandPalette — ⌘K / Ctrl+K quick-jump overlay.
 * When query is empty: filters sidebar navigation links.
 * When query ≥ 2 chars: fetches live results across properties, guests, and bookings.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export interface PaletteLink {
  name: string;
  href: string;
  section: string;
  icon: string;
}

interface PropertyResult {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
}

interface GuestResult {
  name: string;
  email: string;
  phone: string | null;
  stay_count: number;
  last_booking_id: number;
}

interface BookingResult {
  id: number;
  guest_name: string;
  property_name: string;
  check_in_date: string;
  check_out_date: string;
  status: string;
}

interface SearchResults {
  properties: PropertyResult[];
  guests: GuestResult[];
  bookings: BookingResult[];
}

type PaletteItem =
  | { kind: 'nav'; link: PaletteLink }
  | { kind: 'property'; data: PropertyResult }
  | { kind: 'guest'; data: GuestResult }
  | { kind: 'booking'; data: BookingResult };

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  links: PaletteLink[];
}

function itemHref(item: PaletteItem): string {
  switch (item.kind) {
    case 'nav': return item.link.href;
    case 'property': return `/app/properties/${item.data.id}`;
    case 'guest': return `/app/guests?q=${encodeURIComponent(item.data.email)}`;
    case 'booking': return `/app/booking-requests?q=${encodeURIComponent(item.data.guest_name)}`;
  }
}

function itemLabel(item: PaletteItem): string {
  switch (item.kind) {
    case 'nav': return item.link.name;
    case 'property': return item.data.name;
    case 'guest': return item.data.name;
    case 'booking': return item.data.guest_name;
  }
}

function itemIcon(item: PaletteItem): string {
  switch (item.kind) {
    case 'nav': return item.link.icon;
    case 'property': return '🏠';
    case 'guest': return '👤';
    case 'booking': return '📋';
  }
}

function itemSub(item: PaletteItem): string {
  switch (item.kind) {
    case 'nav': return item.link.section;
    case 'property': {
      const parts = [item.data.city, item.data.state].filter(Boolean);
      return parts.length ? parts.join(', ') : (item.data.address ?? '');
    }
    case 'guest': return `${item.data.stay_count} stay${item.data.stay_count !== 1 ? 's' : ''} · ${item.data.email}`;
    case 'booking': return `${item.data.property_name} · ${item.data.check_in_date} → ${item.data.check_out_date}`;
  }
}

function groupLabel(kind: PaletteItem['kind']): string {
  switch (kind) {
    case 'nav': return 'Navigation';
    case 'property': return 'Properties';
    case 'guest': return 'Guests';
    case 'booking': return 'Bookings';
  }
}

export function CommandPalette({ isOpen, onClose, links }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [entityResults, setEntityResults] = useState<SearchResults | null>(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Nav links filtered (always shown when query < 2, or as prefix when querying)
  const navFiltered = query.trim() === ''
    ? links.slice(0, 8)
    : links.filter(l =>
        l.name.toLowerCase().includes(query.toLowerCase()) ||
        l.section.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5);

  // Build flat list for keyboard nav
  const allItems: PaletteItem[] = [
    ...navFiltered.map((l): PaletteItem => ({ kind: 'nav', link: l })),
    ...(entityResults?.properties.map((d): PaletteItem => ({ kind: 'property', data: d })) ?? []),
    ...(entityResults?.guests.map((d): PaletteItem => ({ kind: 'guest', data: d })) ?? []),
    ...(entityResults?.bookings.map((d): PaletteItem => ({ kind: 'booking', data: d })) ?? []),
  ];

  // Debounced entity search
  const runSearch = useCallback(async (q: string) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    setEntityLoading(true);
    try {
      const res = await fetch(`/api/owner/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json() as SearchResults;
        setEntityResults(data);
      }
    } catch {
      // silent — search is additive
    } finally {
      setEntityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setEntityResults(null);
      setEntityLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(query.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setEntityResults(null);
      setEntityLoading(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Keep selected item in view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // Find the list item by data-index attribute
    const item = list.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | null;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, entityResults]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = allItems[selectedIndex];
        if (item) {
          navigate(itemHref(item));
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, allItems, selectedIndex, navigate, onClose]);

  if (!isOpen) return null;

  // Group items for rendering with section headers
  type Group = { kind: PaletteItem['kind']; items: { item: PaletteItem; globalIndex: number }[] };
  const groups: Group[] = [];
  let cursor = 0;
  const addGroup = (kind: PaletteItem['kind'], items: PaletteItem[]) => {
    if (items.length === 0) return;
    groups.push({ kind, items: items.map(item => ({ item, globalIndex: cursor++ })) });
  };
  addGroup('nav', navFiltered.map(l => ({ kind: 'nav' as const, link: l })));
  addGroup('property', entityResults?.properties.map(d => ({ kind: 'property' as const, data: d })) ?? []);
  addGroup('guest', entityResults?.guests.map(d => ({ kind: 'guest' as const, data: d })) ?? []);
  addGroup('booking', entityResults?.bookings.map(d => ({ kind: 'booking' as const, data: d })) ?? []);

  const hasEntityResults = entityResults && (
    entityResults.properties.length + entityResults.guests.length + entityResults.bookings.length > 0
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
      aria-modal="true"
      role="dialog"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <span className="text-gray-400 text-lg flex-shrink-0" aria-hidden="true">
            {entityLoading ? (
              <span className="inline-block w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            ) : '🔍'}
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search properties, guests, bookings…"
            className="flex-1 bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 text-sm outline-none"
            aria-label="Global search"
            aria-autocomplete="list"
            aria-controls="palette-results"
            aria-activedescendant={allItems[selectedIndex] ? `palette-item-${selectedIndex}` : undefined}
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
            esc
          </kbd>
        </div>

        {/* Results */}
        <ul
          id="palette-results"
          ref={listRef}
          role="listbox"
          aria-label="Search results"
          className="max-h-80 overflow-y-auto py-1"
        >
          {allItems.length === 0 && !entityLoading && (
            <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {query.trim().length >= 2
                ? `No results for "${query}"`
                : 'No matching navigation items'}
            </li>
          )}

          {groups.map(group => (
            <li key={group.kind}>
              {/* Section header — only shown when mixing nav + entity results */}
              {(hasEntityResults || query.trim().length >= 2) && group.items.length > 0 && (
                <div className="px-4 pt-2 pb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {groupLabel(group.kind)}
                  </span>
                </div>
              )}
              <ul role="group">
                {group.items.map(({ item, globalIndex }) => (
                  <li
                    key={`${item.kind}-${globalIndex}`}
                    id={`palette-item-${globalIndex}`}
                    data-index={globalIndex}
                    role="option"
                    aria-selected={globalIndex === selectedIndex}
                    onClick={() => {
                      navigate(itemHref(item));
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm transition-colors ${
                      globalIndex === selectedIndex
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <span className="text-base w-5 text-center flex-shrink-0" aria-hidden="true">
                      {itemIcon(item)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="font-medium block truncate">{itemLabel(item)}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 block truncate">
                        {itemSub(item)}
                      </span>
                    </span>
                    {globalIndex === selectedIndex && (
                      <span className="text-gray-400 text-xs flex-shrink-0" aria-hidden="true">↵</span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">↵</kbd> select</span>
          <span><kbd className="font-mono">esc</kbd> close</span>
          {query.trim().length >= 2 && (
            <span className="ml-auto">
              {entityLoading ? 'Searching…' : hasEntityResults ? `${
                (entityResults?.properties.length ?? 0) +
                (entityResults?.guests.length ?? 0) +
                (entityResults?.bookings.length ?? 0)
              } results` : 'Nav only'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
